'use client';

import { IconFrenchFlag, IconPortugueseFlag, IconSpanishFlag, IconUsaFlag } from '@/app/(webapp)/_components/ui/icons';
import { usePathname, useRouter } from '@/app/(webapp)/_i18n/navigation';
import { cn } from '@/app/_shared/lib/utils';
import { routing } from '@/i18n/routing';
import type { Locale } from 'next-intl';
import { useTranslations } from 'next-intl';
import { useParams } from 'next/navigation';
import { JSX, useTransition } from 'react';
import {
    NAVIGATION_ACTIVE_ITEM_CLASS,
    NAVIGATION_ITEM_CLASS,
    NAVIGATION_LAST_ITEM_CLASS,
    NavigationDropDown,
} from './navigation-dropdown';

type Props = {
    defaultValue: string;
    label: string;
    className?: string;
    isDirectionUp?: boolean;
};

const FLAGS: ReadonlyArray<{ locale: string; icon: JSX.Element }> = [
    { locale: 'fr', icon: <IconFrenchFlag className="mr-3" /> },
    { locale: 'pt', icon: <IconPortugueseFlag className="mr-3" /> },
    { locale: 'es', icon: <IconSpanishFlag className="mr-3" /> },
    { locale: 'en', icon: <IconUsaFlag className="mr-3" /> },
];

export const LocaleSwitcherSelect = ({ defaultValue, label, className = '', isDirectionUp }: Props) => {
    const router = useRouter();
    const [isPending, startTransition] = useTransition();
    const pathname = usePathname();
    const params = useParams();
    const t = useTranslations('LocaleSwitcher');

    const currentLocale = routing.locales.find((cur) => defaultValue === cur);

    const onSelectChange = (nextLocale: Locale, close: () => void) => {
        close();
        startTransition(() => {
            router.replace(
                // @ts-expect-error -- TypeScript will validate that only known `params`
                // are used in combination with a given `pathname`. Since the two will
                // always match for the current route, we can skip runtime checks.
                { pathname, params },
                { locale: nextLocale },
            );
        });
    };

    return (
        <NavigationDropDown
            label={<span>{t('locale', { locale: currentLocale || 'fr' })}</span>}
            ariaLabel={label}
            containerClassName={className}
            panelLayout="floating"
            panelPositionClassName={isDirectionUp ? 'left-0 bottom-full mb-2' : 'left-0 top-full mt-2'}
            panelSizeClassName=""
            panelClassName={isPending ? 'pointer-events-none opacity-60' : undefined}
        >
            {(close) =>
                routing.locales.map((cur, index, array) => (
                    <li
                        className={cn(
                            'whitespace-nowrap',
                            cur === currentLocale ? NAVIGATION_ACTIVE_ITEM_CLASS : NAVIGATION_ITEM_CLASS,
                            index === array.length - 1 && NAVIGATION_LAST_ITEM_CLASS,
                        )}
                        key={cur}
                        aria-selected={cur === currentLocale}
                        onClick={() => onSelectChange(cur, close)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                onSelectChange(cur, close);
                            }
                        }}
                    >
                        {FLAGS.find((f) => f.locale === cur)?.icon}
                        {t('locale', { locale: cur })}
                    </li>
                ))
            }
        </NavigationDropDown>
    );
};
